import { useNavigate } from 'react-router-dom';
import { useAppointmentUpdateAndPersist } from '../../../Appointment/appointment';
import { Types, useCartDispatch } from '../../../cart';
import { useLocalizationDispatch } from '../../../Localization/localization';
import { KioskCookies } from '../../../../helpers/Cookies';
import Cookies from 'js-cookie';
import type { SavedSessionCookieData } from '../../SavedSession.types';
import { getAppointment, IAppointment, IService, IXtimeServiceBase, MaintenanceMenu, RNDService, ServiceComment, XTimeServiceProperties } from '../../../../API/GoMoto';
import { ServiceCategory } from '../../../../API/Enums';
import { LocalizationDispatchTypes } from '../../../Localization/localization.types';
import { SavedSessionLoadHook } from './SavedSessionLoad.types';

/**
 * Hook exposing `loadSession`, which restores a previously saved session from
 * the SavedSession cookie. Fetches the appointment by its stored key,
 * rehydrates the cart with confirmed services, xtime services, maintenance
 * menus, RND services, comment, and transportation, applies the saved locale,
 * and navigates to the saved route. If the cookie is missing or unparseable,
 * the cookie is removed and no state changes are made.
 */
export const useSavedSessionLoad: SavedSessionLoadHook = () => {
    const setAppointment = useAppointmentUpdateAndPersist();
    const navigate = useNavigate();
    const cartDispatch = useCartDispatch();
    const localizationDispatch = useLocalizationDispatch();

    return {
        loadSession: async () => {
            const cookie: string | undefined = Cookies.get(KioskCookies.SavedSession);

            if (cookie) {
                let data: SavedSessionCookieData | null = null;

                try {
                    data = JSON.parse(cookie);
                }
                catch {
                    data = null;
                }

                if (data) {
                    setAppointment(await getAppointment(data.appointmentKey))
                        .then((appointment: IAppointment | null) => {
                            if (appointment) {
                                appointment.servicesConfirmed?.forEach((serviceConfirmed: ServiceComment) => {
                                    const fullService: IService = {
                                        id: serviceConfirmed.id,
                                        name: serviceConfirmed.name || '',
                                        nameAltLang: serviceConfirmed.nameAltLang || '',
                                        description: serviceConfirmed.description || '',
                                        descriptionAltLang: serviceConfirmed.descriptionAltLang || '',
                                        price: serviceConfirmed.price,
                                        priceLow: serviceConfirmed.priceLow,
                                        priceHigh: serviceConfirmed.priceHigh,
                                        spgPricing: serviceConfirmed.spgPricing,
                                        category: ServiceCategory.All,
                                        imagePath: '',
                                        imageUrl: '',
                                        isTransport: serviceConfirmed.isTransport,
                                        useSpgPricing: !!serviceConfirmed.spgPricing,
                                        comment: serviceConfirmed.comment,
                                    };

                                    cartDispatch({
                                        type: Types.AddService,
                                        payload: fullService,
                                    });
                                });

                                localizationDispatch({
                                    type: LocalizationDispatchTypes.SetLocale,
                                    payload: appointment.locale,
                                });

                                appointment.xtimeServices?.forEach((xtimeService: Pick<IXtimeServiceBase, XTimeServiceProperties>) => {
                                    cartDispatch({
                                        type: Types.AddXtimeService,
                                        payload: {
                                            id: xtimeService.id,
                                            goMotoId: xtimeService.goMotoId,
                                            type: 'standard',
                                            name: xtimeService.name,
                                            opCode: xtimeService.opCode,
                                            price: xtimeService.price,
                                            description: xtimeService.description,
                                            isCommonRepair: xtimeService.isCommonRepair,
                                            comment: xtimeService.comment,
                                        },
                                    });
                                });

                                appointment.maintenanceMenusConfirmed?.forEach((maintenanceMenuConfirmed: MaintenanceMenu) => {
                                    cartDispatch({
                                        type: Types.AddMaintMenu,
                                        payload: maintenanceMenuConfirmed,
                                    });
                                });

                                appointment.rndServices?.forEach((rndService: RNDService) => {
                                    cartDispatch({
                                        type: Types.AddRNDService,
                                        payload: rndService,
                                    });
                                });

                                appointment.comment && cartDispatch({
                                    type: Types.AddComment,
                                    payload: {
                                        key: 'Services',
                                        comment: appointment.comment,
                                    },
                                });

                                if (appointment.transportation) {
                                    cartDispatch({
                                        type: Types.SetTransportation,
                                        payload: {
                                            type: appointment.transportation.type,
                                            subtype: appointment.transportation.subtype,
                                        },
                                    });
                                }

                                navigate(data.route);
                            }
                        });
                    return;
                }
            }
            Cookies.remove(KioskCookies.SavedSession);
        },
    };
};